import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Card, CardContent, Typography, Alert, Stack, CircularProgress } from '@mui/material';
import { GoogleLogin, type CredentialResponse } from '@react-oauth/google';
import { useAuth } from '../auth/useAuth';
import { palette } from '../theme';
import { ApiError } from '../api/client';

export function LoginPage() {
  const { status, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const next = params.get('next') ?? '/dashboard';

  useEffect(() => {
    if (status === 'authed') navigate(next, { replace: true });
  }, [status, next, navigate]);

  const onCredential = async (response: CredentialResponse) => {
    setError(null);
    if (!response.credential) {
      setError('Google did not return a credential. Please try again.');
      return;
    }
    setSubmitting(true);
    try {
      await loginWithGoogle(response.credential);
      navigate(next, { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 403) {
          const body = err.body as { error?: string; allowedDomain?: string } | null;
          if (body?.error === 'domain_not_allowed') {
            const domain = body.allowedDomain;
            setError(
              domain
                ? `Sign-in is restricted to @${domain} email addresses.`
                : 'Your email address is not on the allowlist for this dashboard.',
            );
          } else {
            setError('Your Google account is not authorized to sign in.');
          }
        } else {
          setError(err.message || 'Sign-in failed.');
        }
      } else {
        setError('Sign-in failed. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'grid',
        placeItems: 'center',
        background: `linear-gradient(180deg, ${palette.sage[50]} 0%, ${palette.neutral.bg} 60%)`,
        p: 2,
      }}
    >
      <Card sx={{ maxWidth: 420, width: '100%', borderRadius: 3 }}>
        <CardContent sx={{ p: 4 }}>
          <Stack spacing={3} alignItems="center">
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 2,
                bgcolor: palette.sage[500],
                color: 'white',
                display: 'grid',
                placeItems: 'center',
                fontWeight: 700,
                fontSize: 22,
              }}
            >
              n
            </Box>
            <Stack spacing={0.5} alignItems="center">
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                natu.care KPI Dashboard
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                Sign in with your work Google account.
              </Typography>
            </Stack>

            {error && (
              <Alert severity="error" sx={{ width: '100%' }}>
                {error}
              </Alert>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'center', minHeight: 44 }}>
              {submitting ? (
                <CircularProgress size={28} />
              ) : (
                <GoogleLogin
                  onSuccess={onCredential}
                  onError={() => setError('Google sign-in was cancelled or failed.')}
                  theme="outline"
                  size="large"
                  shape="pill"
                  text="signin_with"
                />
              )}
            </Box>

            <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
              Access is granted per-tab by an admin. If this is your first time, ask an admin to grant access.
            </Typography>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}
